/*
  Zerafite PDP buy box helpers. Vanilla, deferred, no dependencies.
  Loaded by sections/main-product.liquid.

  1. Quantity counter (snippets/zf-qty-counter.liquid).
     The counter has no name attribute. It writes its value into the one hidden
     quantity input inside the product form, so the add-to-cart FormData built
     in snippets/buy-buttons.liquid always carries exactly one quantity.
  2. Sticky add-to-cart button mirrors the main button's loading state.
  3. "What are you shopping for today?" choice (snippets/zf-shop-intent.liquid).
     Two plain buttons that behave as a radio group and show or hide one
     panel. It reads and writes no form field and no price.
  4. One-sentence rule for attributed quotes. A quote is never reworded: its
     first sentence stays visible and a "Read more" button reveals the full
     original text. Used for the Dr. Baumann statement block on product pages,
     and shared with sections/hero-benefits.liquid through
     window.ZFPdp.firstSentenceEnd.

  This file never posts to the cart. Adding to cart stays in
  snippets/buy-buttons.liquid.
*/
(function () {
  'use strict';

  if (window.__zfPdpLoaded) return;
  window.__zfPdpLoaded = true;

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  // Quantity rules per variant id, from the variants JSON that
  // snippets/buy-buttons.liquid prints. Shopify includes quantity_rule
  // ({ min, max, increment }) on every variant, and it follows the buyer's
  // context, so a B2B catalog rule arrives here without any theme logic.
  var variantRules = null;
  function getVariantRules() {
    if (variantRules) return variantRules;
    variantRules = {};
    var el = document.querySelector('.buy-buttons__variants-json');
    if (!el) return variantRules;
    try {
      var variants = JSON.parse(el.textContent);
      if (Array.isArray(variants)) {
        variants.forEach(function (variant) {
          if (variant && variant.id && variant.quantity_rule) {
            variantRules[String(variant.id)] = variant.quantity_rule;
          }
        });
      }
    } catch (error) {
      variantRules = {};
    }
    return variantRules;
  }

  function initCounter(root) {
    if (root.getAttribute('data-zf-qty-ready') === 'true') return;

    var input = root.querySelector('[data-zf-qty-input]');
    var minus = root.querySelector('[data-zf-qty-minus]');
    var plus = root.querySelector('[data-zf-qty-plus]');
    var ruleEl = root.querySelector('[data-zf-qty-rule]');
    var statusEl = root.querySelector('[data-zf-qty-status]');
    if (!input || !minus || !plus) return;

    root.setAttribute('data-zf-qty-ready', 'true');

    var formId = root.getAttribute('data-zf-form');
    var rule = {
      min: Math.max(1, toInt(root.getAttribute('data-zf-min'), 1)),
      step: Math.max(1, toInt(root.getAttribute('data-zf-step'), 1)),
      max: toInt(root.getAttribute('data-zf-max'), null)
    };

    function getForm() {
      return formId ? document.getElementById(formId) : null;
    }

    // The single quantity field of the product form.
    function getHiddenInput() {
      var form = getForm();
      return form ? form.querySelector('input[name="quantity"]') : null;
    }

    // Valid quantities are min, min + step, min + 2 * step, ... up to max.
    // Anything else snaps down to the nearest valid value, never below min.
    function snap(value) {
      var quantity = toInt(value, rule.min);
      if (quantity < rule.min) quantity = rule.min;
      quantity = rule.min + Math.floor((quantity - rule.min) / rule.step) * rule.step;
      if (rule.max !== null && quantity > rule.max) {
        quantity = rule.min + Math.floor((rule.max - rule.min) / rule.step) * rule.step;
        if (quantity < rule.min) quantity = rule.min;
      }
      return quantity;
    }

    function setDisabled(button, disabled) {
      // aria-disabled, not disabled: a button that disables itself while it has
      // keyboard focus drops that focus.
      if (disabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }

    function write(value, announce) {
      var quantity = snap(value);

      if (String(input.value) !== String(quantity)) input.value = quantity;

      var hidden = getHiddenInput();
      var changed = false;
      if (hidden && String(hidden.value) !== String(quantity)) {
        hidden.value = quantity;
        changed = true;
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setDisabled(minus, quantity <= rule.min);
      setDisabled(plus, rule.max !== null && quantity + rule.step > rule.max);

      if (changed) {
        // Same event the quantity-breaks block used to send, so anything still
        // listening for it (free gifts, checkpoints bar) keeps working.
        document.dispatchEvent(new CustomEvent('quantity-update', {
          detail: { quantity: quantity, source: 'zf-qty-counter', formId: formId || '' }
        }));
      }

      if (announce && statusEl) {
        statusEl.textContent = (root.getAttribute('data-zf-label') || 'Quantity') + ' ' + quantity;
      }

      return quantity;
    }

    function renderRule() {
      input.setAttribute('min', rule.min);
      input.setAttribute('step', rule.step);
      if (rule.max !== null) {
        input.setAttribute('max', rule.max);
      } else {
        input.removeAttribute('max');
      }

      if (!ruleEl) return;
      var parts = [];
      var stepTpl = root.getAttribute('data-zf-tpl-step');
      var minTpl = root.getAttribute('data-zf-tpl-min');
      var maxTpl = root.getAttribute('data-zf-tpl-max');
      if (rule.step > 1 && stepTpl) parts.push(stepTpl.replace('[n]', rule.step));
      if (rule.min > 1 && minTpl) parts.push(minTpl.replace('[n]', rule.min));
      if (rule.max !== null && maxTpl) parts.push(maxTpl.replace('[n]', rule.max));

      ruleEl.textContent = '';
      parts.forEach(function (text) {
        var span = document.createElement('span');
        span.textContent = text;
        ruleEl.appendChild(span);
      });
      ruleEl.hidden = parts.length === 0;
    }

    function applyRuleForVariant(variantId) {
      var next = getVariantRules()[String(variantId)];
      if (!next) return;
      rule = {
        min: Math.max(1, toInt(next.min, 1)),
        step: Math.max(1, toInt(next.increment, 1)),
        max: next.max === null || next.max === undefined ? null : toInt(next.max, null)
      };
      renderRule();
      write(input.value, false);
    }

    minus.addEventListener('click', function () {
      if (minus.getAttribute('aria-disabled') === 'true') return;
      write(toInt(input.value, rule.min) - rule.step, true);
    });

    plus.addEventListener('click', function () {
      if (plus.getAttribute('aria-disabled') === 'true') return;
      write(toInt(input.value, rule.min) + rule.step, true);
    });

    // Typing: commit on change (blur, Enter, native arrows). While the shopper
    // is still typing, only pass through values that are already valid, so the
    // field is not rewritten under their fingers.
    input.addEventListener('change', function () {
      write(input.value, false);
    });

    input.addEventListener('input', function () {
      var typed = toInt(input.value, null);
      if (typed !== null && String(snap(typed)) === String(typed)) write(typed, false);
    });

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        write(input.value, true);
      }
    });

    // Variant changes: main.js writes the new variant id into the form's hidden
    // id input and dispatches a bubbling change event on it.
    // Note: never read form.id here. The form holds <input name="id">, so
    // form.id is that input element, not the id string.
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || target.name !== 'id' || !target.form) return;
      if (target.form.getAttribute('id') !== formId) return;
      applyRuleForVariant(target.value);
    });

    // Initial sync. If the form is not in the DOM yet, wait for it.
    function initialSync() {
      var form = getForm();
      if (!form) return false;
      var idInput = form.querySelector('input[name="id"]');
      if (idInput && idInput.value && getVariantRules()[String(idInput.value)]) {
        applyRuleForVariant(idInput.value);
      } else {
        renderRule();
        write(input.value, false);
      }
      return true;
    }

    if (!initialSync()) {
      var tries = 0;
      var timer = setInterval(function () {
        tries += 1;
        if (initialSync() || tries > 40) clearInterval(timer);
      }, 100);
    }
  }

  function initCounters() {
    document.querySelectorAll('[data-zf-qty]').forEach(initCounter);
  }

  // The sticky button submits the main form (layout/theme.liquid), and the
  // submit handler only marks the main button as loading. Mirror that state so
  // a tap on the sticky bar gets visible feedback too.
  function mirrorStickyLoading() {
    document.querySelectorAll('[id^="SectionAtcBtn-"]').forEach(function (sticky) {
      if (sticky.getAttribute('data-zf-mirror') === 'true') return;
      var sectionId = sticky.id.slice('SectionAtcBtn-'.length);
      var main = document.getElementById('ProductSubmitButton-' + sectionId);
      if (!main || typeof MutationObserver === 'undefined') return;
      sticky.setAttribute('data-zf-mirror', 'true');

      new MutationObserver(function () {
        var loading = main.classList.contains('loading');
        if (loading === sticky.classList.contains('loading')) return;
        sticky.classList.toggle('loading', loading);
        if (loading) {
          sticky.setAttribute('aria-busy', 'true');
        } else {
          sticky.removeAttribute('aria-busy');
        }
      }).observe(main, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // Shopping choice. The options are <button type="button" role="radio"> on
  // purpose, not radio inputs: they sit inside <product-info>, and a native
  // input there would fire change events that the theme's product code and the
  // subscription bridge also listen for. Arrow keys move and select, as they do
  // in a native radio group; Space and Enter work through the button's click.
  function initIntent(root) {
    if (root.getAttribute('data-zf-intent-ready') === 'true') return;

    var options = Array.prototype.slice.call(root.querySelectorAll('[data-zf-intent-option]'));
    var panel = root.querySelector('[data-zf-intent-panel]');
    if (options.length < 2 || !panel) return;

    root.setAttribute('data-zf-intent-ready', 'true');

    function select(option, moveFocus) {
      var choice = option.getAttribute('data-zf-intent-option');
      options.forEach(function (other) {
        var isSelected = other === option;
        other.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        other.setAttribute('tabindex', isSelected ? '0' : '-1');
      });
      // Only the panel is shown or hidden. Prices and the buy box are never touched.
      panel.hidden = choice !== 'routine';
      root.setAttribute('data-zf-intent-choice', choice);
      if (moveFocus) option.focus();
    }

    options.forEach(function (option, index) {
      option.addEventListener('click', function () {
        select(option, false);
      });

      option.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = options[(index + 1) % options.length];
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = options[(index - 1 + options.length) % options.length];
        } else if (event.key === 'Home') {
          next = options[0];
        } else if (event.key === 'End') {
          next = options[options.length - 1];
        }
        if (next) {
          event.preventDefault();
          select(next, true);
        }
      });
    });
  }

  function initIntents() {
    document.querySelectorAll('[data-zf-intent]').forEach(initIntent);
  }

  // Words that end in a full stop without ending a sentence.
  var ABBREVIATIONS = /^(dr|mr|mrs|ms|prof|st|vs|no|fig|e\.g|i\.e|u\.s|u\.s\.a|ph\.d|m\.d|fl|oz|approx|inc|co|ltd|jr|sr|etc|a\.m|p\.m)$/i;

  // Returns the index just after the first sentence of the text (a closing
  // quote mark or bracket included), or -1 when the text is one sentence.
  // A sentence ends at . ! ? or an ellipsis followed by whitespace and then a
  // capital letter or a digit. "Dr. Baumann", "Ph.D. in", "J. Smith" and
  // "4.8" do not end one. When in doubt it does not split, so the worst case
  // is that more text stays visible, never that a sentence is cut in half.
  function firstSentenceEnd(text) {
    text = String(text === undefined || text === null ? '' : text);
    var re = /([.!?…]+)(["'”’)\]]*)\s+(?=["'“‘(\[]*[A-Z0-9])/g;
    var match;
    while ((match = re.exec(text)) !== null) {
      var marks = match[1];
      if (marks === '.') {
        var before = /(\S+)$/.exec(text.slice(0, match.index));
        var word = before ? before[1].replace(/^["'“‘(\[]+/, '') : '';
        if (word.length <= 1 || ABBREVIATIONS.test(word)) continue;
      }
      var end = match.index + marks.length + match[2].length;
      return /\S/.test(text.slice(end)) ? end : -1;
    }
    return -1;
  }

  window.ZFPdp = window.ZFPdp || {};
  window.ZFPdp.firstSentenceEnd = firstSentenceEnd;

  // Maps a character index in root.textContent to a text node and an offset.
  function textPosition(root, index) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var seen = 0;
    var node = walker.nextNode();
    while (node) {
      var length = node.nodeValue.length;
      if (index <= seen + length) return { node: node, offset: index - seen };
      seen += length;
      node = walker.nextNode();
    }
    return null;
  }

  // Long attributed quote: keep the first sentence visible and put the rest
  // behind a "Read more" button. Nothing is removed from the page and no word
  // is changed: the excerpt is a copy of the start of the original paragraph
  // (inline formatting kept), and the original paragraph is shown as it was
  // typed when the button is used. Without this script the full text shows.
  var quoteSerial = 0;
  function initQuote(box) {
    if (box.getAttribute('data-zf-quote-ready') === 'true') return;
    box.setAttribute('data-zf-quote-ready', 'true');
    // Already inside a disclosure built in Liquid: leave it alone.
    if (box.closest('details')) return;

    var parts = Array.prototype.slice.call(box.children);
    var target = null;
    var end = -1;
    for (var i = 0; i < parts.length; i++) {
      end = firstSentenceEnd(parts[i].textContent);
      if (end > -1) {
        target = parts[i];
        break;
      }
    }
    if (!target) return;

    var position = textPosition(target, end);
    if (!position) return;

    var range = document.createRange();
    range.setStart(target, 0);
    range.setEnd(position.node, position.offset);

    quoteSerial += 1;
    if (!target.id) target.id = 'zf-quote-full-' + quoteSerial;

    var excerpt = document.createElement(target.tagName === 'P' ? 'p' : 'div');
    excerpt.className = 'zf-quote__excerpt';
    excerpt.appendChild(range.cloneContents());

    // A long quote opens with a quote mark that only closes at its very end.
    // The excerpt stops before that, so its lone opening mark is left off;
    // the full quote, shown by "Read more", keeps both marks as typed.
    var excerptText = excerpt.textContent;
    if (/^\s*["'“‘]/.test(excerptText) && !/["'”’]\s*$/.test(excerptText)) {
      var excerptWalker = document.createTreeWalker(excerpt, NodeFilter.SHOW_TEXT, null);
      var firstText = excerptWalker.nextNode();
      while (firstText && !/\S/.test(firstText.nodeValue)) firstText = excerptWalker.nextNode();
      if (firstText) firstText.nodeValue = firstText.nodeValue.replace(/^(\s*)["'“‘]+/, '$1');
    }

    // The long paragraph and everything after it wait behind the button.
    var held = parts.slice(parts.indexOf(target));

    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'zf-quote__toggle';
    toggle.setAttribute('aria-controls', target.id);

    var wrap = document.createElement('p');
    wrap.className = 'zf-quote__more';
    wrap.appendChild(toggle);

    function setOpen(open) {
      held.forEach(function (part) {
        part.hidden = !open;
        part.classList.toggle('zf-quote-hidden', !open);
      });
      excerpt.hidden = open;
      excerpt.classList.toggle('zf-quote-hidden', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open ? 'Show less' : 'Read more';
    }

    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    box.insertBefore(excerpt, target);
    box.appendChild(wrap);
    setOpen(false);
  }

  function initQuotes() {
    // The statement block (blocks/ai_gen_block_46fbb1c.liquid) prints its text
    // in an element whose class starts with "ai-brand-story-description-".
    document.querySelectorAll('[class*="ai-brand-story-description-"], [data-zf-quote]').forEach(initQuote);
  }

  function init() {
    variantRules = null;
    initCounters();
    mirrorStickyLoading();
    initIntents();
    initQuotes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: the section is re-rendered when a setting changes.
  document.addEventListener('shopify:section:load', init);
})();
